import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useLang } from '@/contexts/LangContext';
import { LANGS } from '@/lib/i18n';

const MENU_WIDTH_PX = 192; // w-48 — keep in sync with className below

/**
 * Language picker dropdown.
 * Shows the currently selected language's native name on the trigger and a
 * panel listing every language (native + English label) — designed to scale
 * cleanly as we add Tamil, Kannada, Marathi etc.
 */
export default function LanguageSwitcher() {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  // 'left' anchors the menu to the trigger's left edge (opens to the right).
  // 'right' anchors to the trigger's right edge (opens to the left).
  // Decided dynamically per-open so the menu never spills off-screen.
  const [side, setSide] = useState('left');
  const rootRef = useRef(null);

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  // Click-outside / Escape to close.
  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && rootRef.current) {
        // Pick the anchoring side that keeps the menu fully inside the
        // viewport. Fall back to 'left' when there's enough room either way.
        const rect = rootRef.current.getBoundingClientRect();
        const vw = window.innerWidth || document.documentElement.clientWidth;
        const roomRight = vw - rect.left;
        setSide(roomRight >= MENU_WIDTH_PX + 8 ? 'left' : 'right');
      }
      return next;
    });
  };

  const pick = (code) => {
    setLang(code);
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={rootRef} data-testid="language-switcher">
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-1.5 bg-white border-2 border-[#E5E5E5] rounded-full pl-3 pr-2 py-1.5 text-sm font-extrabold text-[#1F2937] hover:border-[#1CB0F6] transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-switcher-trigger"
      >
        <Globe size={14} strokeWidth={3} className="text-[#1CB0F6]" />
        <span data-testid="language-switcher-current">{current.native}</span>
        <ChevronDown
          size={14}
          strokeWidth={3}
          className={`text-[#94A3B8] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className={`absolute top-full mt-2 w-48 max-h-72 overflow-y-auto bg-white border-2 border-[#E5E5E5] rounded-2xl shadow-lg py-1 z-50 ${
            side === 'right' ? 'right-0' : 'left-0'
          }`}
          data-testid="language-switcher-menu"
        >
          {LANGS.map((l) => {
            const selected = l.code === lang;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => pick(l.code)}
                  role="option"
                  aria-selected={selected}
                  data-testid={`lang-${l.code}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-extrabold hover:bg-[#F0F8FF] transition-colors ${
                    selected ? 'text-[#1CB0F6]' : 'text-[#1F2937]'
                  }`}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{l.native}</span>
                    {l.native !== l.name && (
                      <span className="block text-[10px] uppercase font-bold text-[#94A3B8] tracking-wider">
                        {l.name}
                      </span>
                    )}
                  </span>
                  {selected && <Check size={16} strokeWidth={3} className="text-[#1CB0F6] flex-shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
